import React from 'react';
import classNames from 'classnames';
import { ButtonProps } from './Button.types';

/**
 * Enhanced Button component that follows the design system
 * Supports multiple variants, sizes, loading states, and icons
 */
export const Button = ({
  children,
  value,
  variant = 'primary',
  size = 'md',
  onClick,
  disabled = false,
  loading = false,
  fullWidth = false,
  startIcon,
  endIcon,
  type = 'button',
  className = '',
  testId,
  ...rest
}: ButtonProps): React.ReactElement => {
  // Determine the content to display (children or value)
  const content = children || value;
  const hasContent = Boolean(content);
  
  // Size classes based on design system
  const sizeClasses = {
    sm: 'text-sm py-2 px-3',
    md: 'text-sm md:text-base py-2.5 px-4',
    lg: 'text-base md:text-lg py-3.5 px-6',
  };
  
  // Variant classes using design system colors
  const variantClasses = {
    primary: 'bg-primary-100 text-white shadow-sm hover:bg-primary-70 hover:shadow focus:ring-primary-70 dark:bg-primary-70 dark:hover:bg-primary-100',
    secondary: 'bg-white/90 dark:bg-slate-800/80 border border-primary-100/30 dark:border-white/15 text-primary-100 dark:text-gray-100 hover:bg-primary-10 dark:hover:bg-slate-700/80 focus:ring-primary-70',
    tertiary: 'bg-transparent text-primary-100 dark:text-gray-200 hover:bg-primary-10 dark:hover:bg-slate-700/70 focus:ring-primary-70'
  };
  
  // Loading indicator with design system colors
  const loadingIndicator = (
    <svg className={classNames('animate-spin -ml-1 h-4 w-4', { 'mr-2': hasContent })} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );
  
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={classNames(
        'font-semibold tracking-wide rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
        sizeClasses[size],
        variantClasses[variant],
        {
          'w-full': fullWidth,
          'opacity-60 cursor-not-allowed': disabled,
          'inline-flex items-center justify-center': startIcon || endIcon || loading,
        },
        className
      )}
      onClick={(e) => {
        if (onClick && !disabled && !loading) {
          onClick(e);
        }
      }}
      data-testid={testId}
      {...rest}
    >
      {loading && loadingIndicator}
      {!loading && startIcon && <span className={classNames({ 'mr-2': hasContent })}>{startIcon}</span>}
      {content}
      {!loading && endIcon && <span className={classNames({ 'ml-2': hasContent })}>{endIcon}</span>}
    </button>
  );
};

export default Button;